import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Badge } from './ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Textarea } from './ui/textarea';
import { Loader2, Plus, Save, Sparkles, Trash2, Link2, Mic, Square, Play, Download } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const createSolution = () => ({
  id: `${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
  solution_name: 'New Voice Solution',
  industry: '',
  ai_voice_id: '',
  ai_voice_name: '',
  use_case: 'inbound_qualification',
  status: 'draft',
  goal: '',
  opening_script: '',
  qualification_questions: [],
  objection_handling: [],
  transfer_trigger_phrases: [],
  closing_script: '',
  disposition_tags: [],
});

const toMultiline = (items = []) => (items || []).join('\n');
const fromMultiline = (text = '') => String(text).split('\n').map((v) => v.trim()).filter(Boolean);

export default function VoiceSolutionBuilderManager() {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [assigning, setAssigning] = useState(false);
  const [solutions, setSolutions] = useState([]);
  const [presets, setPresets] = useState([]);
  const [campaigns, setCampaigns] = useState([]);
  const [availableVoices, setAvailableVoices] = useState([]);
  const [voiceProviderState, setVoiceProviderState] = useState({ has_key: false, message: '' });
  const [selectedSolutionId, setSelectedSolutionId] = useState(null);
  const [selectedPresetId, setSelectedPresetId] = useState('');
  const [selectedCampaignId, setSelectedCampaignId] = useState('');
  const [assignMode, setAssignMode] = useState('append');
  const [campaignGroup, setCampaignGroup] = useState('live_online');
  const [deliveryStrategy, setDeliveryStrategy] = useState('first_open');
  const [deliveryFallback, setDeliveryFallback] = useState('round_robin');
  const [interactionActive, setInteractionActive] = useState(false);
  const [isListening, setIsListening] = useState(false);
  const [interactionLog, setInteractionLog] = useState([]);
  const [previewClips, setPreviewClips] = useState([]);
  const [isGeneratingVoice, setIsGeneratingVoice] = useState(false);
  const [simContext, setSimContext] = useState({ caller_name: '', city: '', issue: '' });

  const recognitionRef = useRef(null);
  const questionIndexRef = useRef(0);
  const interactionActiveRef = useRef(false);

  const getHeaders = useCallback(() => {
    const token = localStorage.getItem('dme_token');
    return { Authorization: `Bearer ${token}` };
  }, []);

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const [solutionsRes, presetsRes, campaignsRes, voicesRes] = await Promise.all([
        axios.get(`${API_URL}/api/admin/voice-solutions`, { headers: getHeaders() }),
        axios.get(`${API_URL}/api/dev-settings/voice-ai/presets`, { headers: getHeaders() }),
        axios.get(`${API_URL}/api/admin/campaigns`, { headers: getHeaders(), params: { limit: 500 } }),
        axios.get(`${API_URL}/api/admin/voice-ai/voices`, { headers: getHeaders() }),
      ]);

      const nextSolutions = solutionsRes.data?.voice_solutions || [];
      const nextPresets = presetsRes.data?.presets || [];
      const nextCampaigns = campaignsRes.data || [];
      const nextVoices = voicesRes.data?.voices || [];

      setSolutions(nextSolutions);
      setPresets(nextPresets);
      setCampaigns(nextCampaigns);
      setAvailableVoices(nextVoices);
      setVoiceProviderState({
        has_key: voicesRes.data?.has_key === true,
        message: voicesRes.data?.message || '',
      });

      if (nextPresets.length) setSelectedPresetId((prev) => prev || nextPresets[0].preset_id);
      if (nextCampaigns.length) setSelectedCampaignId((prev) => prev || nextCampaigns[0].id);
      if (nextSolutions.length) setSelectedSolutionId((prev) => prev || nextSolutions[0].id);
      else setSelectedSolutionId(null);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to load voice solutions');
    } finally {
      setLoading(false);
    }
  }, [getHeaders]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const activeSolution = useMemo(
    () => solutions.find((item) => item.id === selectedSolutionId) || null,
    [selectedSolutionId, solutions]
  );

  const campaignsUsingActive = useMemo(() => {
    if (!activeSolution?.id) return [];
    return campaigns.filter((c) => (c.voice_solution_ids || []).includes(activeSolution.id));
  }, [activeSolution?.id, campaigns]);

  const filteredCampaigns = useMemo(() => {
    if (campaignGroup === 'live_online') {
      return campaigns.filter((campaign) => String(campaign.lead_type || '').toLowerCase() === 'live_transfer');
    }
    return campaigns;
  }, [campaignGroup, campaigns]);

  useEffect(() => {
    if (!filteredCampaigns.length) {
      setSelectedCampaignId('');
      return;
    }
    const exists = filteredCampaigns.some((campaign) => campaign.id === selectedCampaignId);
    if (!exists) {
      setSelectedCampaignId(filteredCampaigns[0].id);
    }
  }, [filteredCampaigns, selectedCampaignId]);

  const updateActiveSolution = (patch) => {
    if (!selectedSolutionId) return;
    setSolutions((prev) => prev.map((solution) => solution.id === selectedSolutionId ? { ...solution, ...patch } : solution));
  };

  const addNewSolution = () => {
    const next = createSolution();
    setSolutions((prev) => [...prev, next]);
    setSelectedSolutionId(next.id);
  };

  const addPresetSolution = () => {
    const preset = presets.find((item) => item.preset_id === selectedPresetId);
    if (!preset) {
      toast.error('Select a preset first');
      return;
    }
    const next = { ...createSolution(), ...preset, id: `${Date.now()}-${Math.random().toString(36).slice(2, 7)}` };
    setSolutions((prev) => [...prev, next]);
    setSelectedSolutionId(next.id);
    toast.success('Preset solution added');
  };

  const deleteActiveSolution = () => {
    if (!activeSolution) return;
    setSolutions((prev) => prev.filter((item) => item.id !== activeSolution.id));
    setSelectedSolutionId(null);
  };

  const addObjection = () => {
    if (!activeSolution) return;
    updateActiveSolution({ objection_handling: [...(activeSolution.objection_handling || []), { objection: '', response: '' }] });
  };

  const updateObjection = (index, key, value) => {
    if (!activeSolution) return;
    updateActiveSolution({
      objection_handling: (activeSolution.objection_handling || []).map((row, idx) => idx === index ? { ...row, [key]: value } : row),
    });
  };

  const removeObjection = (index) => {
    if (!activeSolution) return;
    updateActiveSolution({ objection_handling: (activeSolution.objection_handling || []).filter((_, idx) => idx !== index) });
  };

  const saveSolutions = async () => {
    try {
      setSaving(true);
      const res = await axios.put(`${API_URL}/api/admin/voice-solutions`, { voice_solutions: solutions }, { headers: getHeaders() });
      setSolutions(res.data?.voice_solutions || solutions);
      toast.success('Voice solutions saved');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save voice solutions');
    } finally {
      setSaving(false);
    }
  };

  const assignToCampaign = async () => {
    if (!activeSolution?.id || !selectedCampaignId) {
      toast.error('Select a solution and campaign first');
      return;
    }

    try {
      setAssigning(true);
      await axios.post(
        `${API_URL}/api/admin/campaigns/${selectedCampaignId}/voice-solutions`,
        {
          voice_solution_ids: [activeSolution.id],
          mode: assignMode,
          campaign_group: campaignGroup,
          delivery_strategy: deliveryStrategy,
          fallback_strategy: deliveryFallback,
        },
        { headers: getHeaders() }
      );
      toast.success('Voice solution assigned to campaign');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to assign voice solution to campaign');
    } finally {
      setAssigning(false);
    }
  };

  const appendInteraction = (speaker, text) => {
    if (!text) return;
    setInteractionLog((prev) => [
      ...prev,
      {
        id: `${Date.now()}-${Math.random().toString(36).slice(2, 6)}`,
        speaker,
        text,
        created_at: new Date().toISOString(),
      },
    ]);
  };

  const activeVoiceId = useMemo(() => {
    if (activeSolution?.ai_voice_id) return activeSolution.ai_voice_id;
    return availableVoices[0]?.voice_id || '';
  }, [activeSolution?.ai_voice_id, availableVoices]);

  const generateVoicePreview = async (text, source = 'agent') => {
    if (!text || !activeVoiceId) return null;
    try {
      setIsGeneratingVoice(true);
      const res = await axios.post(
        `${API_URL}/api/admin/voice-ai/tts-preview`,
        {
          text,
          voice_id: activeVoiceId,
          model_id: 'eleven_multilingual_v2',
        },
        { headers: getHeaders() }
      );

      const clip = {
        id: res.data?.preview_id || `clip-${Date.now()}`,
        text,
        source,
        audio_url: res.data?.audio_url,
      };
      if (clip.audio_url) {
        setPreviewClips((prev) => [clip, ...prev]);
        const audio = new Audio(clip.audio_url);
        audio.play().catch(() => {});
      }
      return clip;
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Voice preview failed');
      return null;
    } finally {
      setIsGeneratingVoice(false);
    }
  };

  const pickAgentResponse = useCallback((callerText) => {
    if (!activeSolution) return '';
    const transcript = String(callerText || '').toLowerCase();

    const objectionRows = activeSolution.objection_handling || [];
    for (const row of objectionRows) {
      const phrase = String(row?.objection || '').toLowerCase().trim();
      if (!phrase) continue;
      if (transcript.includes(phrase)) {
        return row?.response || '';
      }
    }

    const questions = activeSolution.qualification_questions || [];
    if (questionIndexRef.current < questions.length) {
      const nextQuestion = questions[questionIndexRef.current];
      questionIndexRef.current += 1;
      return nextQuestion;
    }

    return activeSolution.closing_script || 'Thanks — I am connecting you now.';
  }, [activeSolution]);

  const processCallerTranscript = useCallback(async (transcript) => {
    appendInteraction('caller', transcript);
    const responseText = pickAgentResponse(transcript);
    appendInteraction('agent', responseText);
    await generateVoicePreview(responseText, 'agent');
  }, [pickAgentResponse]);

  const stopInteraction = useCallback(() => {
    setInteractionActive(false);
    interactionActiveRef.current = false;
    setIsListening(false);
    questionIndexRef.current = 0;
    if (recognitionRef.current) {
      try {
        recognitionRef.current.onresult = null;
        recognitionRef.current.onerror = null;
        recognitionRef.current.onend = null;
        recognitionRef.current.stop();
      } catch (error) {
        // no-op
      }
      recognitionRef.current = null;
    }
  }, []);

  const startInteraction = async () => {
    if (!activeSolution) {
      toast.error('Select a voice solution first');
      return;
    }
    if (!activeVoiceId) {
      toast.error('Choose an AI voice first');
      return;
    }

    const applyContextTokens = (text) => String(text || '')
      .replaceAll('{{caller_name}}', simContext.caller_name || 'there')
      .replaceAll('{{city}}', simContext.city || 'your area')
      .replaceAll('{{issue}}', simContext.issue || 'your request');

    const openingScript = applyContextTokens(activeSolution.opening_script || 'Hello! How can I help you today?');
    setInteractionLog([]);
    questionIndexRef.current = 0;
    setInteractionActive(true);
    interactionActiveRef.current = true;

    appendInteraction('agent', openingScript);
    await generateVoicePreview(openingScript, 'agent');

    const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SpeechRecognition) {
      toast.error('Browser speech recognition is not supported here. Try Chrome.');
      return;
    }

    const recognition = new SpeechRecognition();
    recognition.lang = 'en-US';
    recognition.interimResults = false;
    recognition.continuous = true;

    recognition.onresult = async (event) => {
      const transcript = Array.from(event.results)
        .slice(event.resultIndex)
        .map((result) => result[0]?.transcript || '')
        .join(' ')
        .trim();

      if (!transcript) return;
      await processCallerTranscript(transcript);
    };

    recognition.onerror = () => {
      setIsListening(false);
    };
    recognition.onend = () => {
      if (interactionActiveRef.current) {
        try {
          recognition.start();
          setIsListening(true);
        } catch (error) {
          setIsListening(false);
        }
      }
    };

    recognitionRef.current = recognition;
    try {
      recognition.start();
      setIsListening(true);
    } catch (error) {
      toast.error('Unable to start microphone interaction');
    }
  };

  const downloadClip = (clip) => {
    if (!clip?.audio_url) return;
    const anchor = document.createElement('a');
    anchor.href = clip.audio_url;
    anchor.download = `${clip.id || 'voice-preview'}.mp3`;
    anchor.click();
  };

  const deleteClip = async (clipId) => {
    setPreviewClips((prev) => prev.filter((clip) => clip.id !== clipId));
    try {
      await axios.delete(`${API_URL}/api/admin/voice-ai/tts-preview/${clipId}`, { headers: getHeaders() });
    } catch (error) {
      // ignore backend delete failures for local UX
    }
  };

  useEffect(() => () => stopInteraction(), [stopInteraction]);

  if (loading) {
    return <div className="p-10 flex justify-center" data-testid="voice-solutions-builder-loading"><Loader2 className="w-8 h-8 animate-spin" /></div>;
  }

  return (
    <div className="space-y-6" data-testid="voice-solutions-builder-page">
      <Card>
        <CardHeader>
          <CardTitle>Voice Solution Builder</CardTitle>
          <CardDescription>Create many industry-specific call solutions and map them to campaigns.</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex flex-wrap gap-2 items-center">
            <Badge variant="outline" data-testid="voice-solutions-builder-count-badge">Solutions: {solutions.length}</Badge>
            <Badge variant="outline" data-testid="voice-solutions-builder-campaign-count-badge">Campaigns: {campaigns.length}</Badge>
          </div>

          <div className="flex gap-2 flex-wrap items-center" data-testid="voice-solutions-builder-controls">
            <Select value={selectedPresetId} onValueChange={setSelectedPresetId}>
              <SelectTrigger className="w-72" data-testid="voice-solutions-preset-select"><SelectValue placeholder="Select preset" /></SelectTrigger>
              <SelectContent>
                {presets.map((preset) => (<SelectItem key={preset.preset_id} value={preset.preset_id}>{preset.solution_name}</SelectItem>))}
              </SelectContent>
            </Select>
            <Button variant="outline" onClick={addPresetSolution} data-testid="voice-solutions-add-preset-button"><Sparkles className="w-4 h-4 mr-2" />Add Preset</Button>
            <Button variant="outline" onClick={addNewSolution} data-testid="voice-solutions-add-new-button"><Plus className="w-4 h-4 mr-2" />New Solution</Button>
            <Button onClick={saveSolutions} disabled={saving} data-testid="voice-solutions-save-button">{saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Save className="w-4 h-4 mr-2" />}Save Solutions</Button>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-12 gap-4">
            <div className="md:col-span-4 space-y-2" data-testid="voice-solutions-list">
              {solutions.length === 0 ? (
                <div className="border rounded p-3 text-sm text-muted-foreground" data-testid="voice-solutions-empty">No solutions yet.</div>
              ) : solutions.map((solution) => (
                <button
                  key={solution.id}
                  type="button"
                  data-testid={`voice-solutions-item-${solution.id}`}
                  className={`w-full border rounded p-3 text-left ${selectedSolutionId === solution.id ? 'border-primary bg-primary/5' : ''}`}
                  onClick={() => setSelectedSolutionId(solution.id)}
                >
                  <p className="font-medium text-sm truncate">{solution.solution_name}</p>
                  <p className="text-xs text-muted-foreground truncate">{solution.industry || 'No industry'} • {solution.use_case || '-'}</p>
                </button>
              ))}
            </div>

            <div className="md:col-span-8 space-y-3" data-testid="voice-solutions-editor">
              {!activeSolution ? (
                <div className="border rounded p-4 text-sm text-muted-foreground" data-testid="voice-solutions-no-selected">Select a solution to edit.</div>
              ) : (
                <>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    <div><Label>Name</Label><Input data-testid="voice-solutions-name-input" value={activeSolution.solution_name || ''} onChange={(e) => updateActiveSolution({ solution_name: e.target.value })} /></div>
                    <div><Label>Industry</Label><Input data-testid="voice-solutions-industry-input" value={activeSolution.industry || ''} onChange={(e) => updateActiveSolution({ industry: e.target.value })} /></div>
                    <div>
                      <Label>Use Case</Label>
                      <Select value={activeSolution.use_case || 'inbound_qualification'} onValueChange={(value) => updateActiveSolution({ use_case: value })}>
                        <SelectTrigger data-testid="voice-solutions-use-case-select"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="inbound_qualification">Inbound Qualification</SelectItem>
                          <SelectItem value="outbound_followup">Outbound Follow-up</SelectItem>
                          <SelectItem value="appointment_booking">Appointment Booking</SelectItem>
                          <SelectItem value="reactivation">Reactivation</SelectItem>
                          <SelectItem value="data_lead_calling">Data Lead Calling</SelectItem>
                          <SelectItem value="live_transfers">Live Transfers</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label>Status</Label>
                      <Select value={activeSolution.status || 'draft'} onValueChange={(value) => updateActiveSolution({ status: value })}>
                        <SelectTrigger data-testid="voice-solutions-status-select"><SelectValue /></SelectTrigger>
                        <SelectContent><SelectItem value="draft">Draft</SelectItem><SelectItem value="active">Active</SelectItem><SelectItem value="paused">Paused</SelectItem></SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label>AI Voice</Label>
                      <Select
                        value={activeSolution.ai_voice_id || activeVoiceId || ''}
                        onValueChange={(value) => {
                          const selectedVoice = availableVoices.find((voice) => voice.voice_id === value);
                          updateActiveSolution({
                            ai_voice_id: value,
                            ai_voice_name: selectedVoice?.name || '',
                          });
                        }}
                      >
                        <SelectTrigger data-testid="voice-solutions-ai-voice-select"><SelectValue placeholder="Select ElevenLabs voice" /></SelectTrigger>
                        <SelectContent>
                          {availableVoices.map((voice) => (
                            <SelectItem key={voice.voice_id} value={voice.voice_id}>{voice.name || voice.voice_id}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="md:col-span-2">
                      {(!voiceProviderState.has_key || voiceProviderState.message || availableVoices.length === 0) && (
                        <p className="text-xs text-amber-700" data-testid="voice-solutions-ai-voice-key-warning">
                          {voiceProviderState.message || 'Add ElevenLabs key in Admin Settings → Voice AI Keys to use live voice preview.'}
                        </p>
                      )}
                    </div>
                  </div>

                  <div><Label>Goal</Label><Input data-testid="voice-solutions-goal-input" value={activeSolution.goal || ''} onChange={(e) => updateActiveSolution({ goal: e.target.value })} /></div>
                  <div><Label>Opening Script</Label><Textarea rows={3} data-testid="voice-solutions-opening-script-input" value={activeSolution.opening_script || ''} onChange={(e) => updateActiveSolution({ opening_script: e.target.value })} /></div>
                  <div><Label>Qualification Questions (one per line)</Label><Textarea rows={3} data-testid="voice-solutions-questions-input" value={toMultiline(activeSolution.qualification_questions)} onChange={(e) => updateActiveSolution({ qualification_questions: fromMultiline(e.target.value) })} /></div>

                  <div className="space-y-2" data-testid="voice-solutions-objections-section">
                    <div className="flex items-center justify-between">
                      <Label>Objection Handling</Label>
                      <Button variant="outline" size="sm" onClick={addObjection} data-testid="voice-solutions-add-objection-button"><Plus className="w-3 h-3 mr-1" />Add</Button>
                    </div>
                    {(activeSolution.objection_handling || []).map((row, idx) => (
                      <div key={`objection-${idx}`} className="grid grid-cols-1 md:grid-cols-12 gap-2" data-testid={`voice-solutions-objection-row-${idx}`}>
                        <Input className="md:col-span-4" data-testid={`voice-solutions-objection-text-${idx}`} value={row.objection || ''} onChange={(e) => updateObjection(idx, 'objection', e.target.value)} placeholder="Objection" />
                        <Input className="md:col-span-7" data-testid={`voice-solutions-objection-response-${idx}`} value={row.response || ''} onChange={(e) => updateObjection(idx, 'response', e.target.value)} placeholder="Response" />
                        <Button className="md:col-span-1" variant="ghost" onClick={() => removeObjection(idx)} data-testid={`voice-solutions-objection-remove-${idx}`}><Trash2 className="w-4 h-4" /></Button>
                      </div>
                    ))}
                  </div>

                  <div><Label>Transfer Trigger Phrases (one per line)</Label><Textarea rows={3} data-testid="voice-solutions-transfer-triggers-input" value={toMultiline(activeSolution.transfer_trigger_phrases)} onChange={(e) => updateActiveSolution({ transfer_trigger_phrases: fromMultiline(e.target.value) })} /></div>
                  <div><Label>Closing Script</Label><Textarea rows={3} data-testid="voice-solutions-closing-script-input" value={activeSolution.closing_script || ''} onChange={(e) => updateActiveSolution({ closing_script: e.target.value })} /></div>
                  <div><Label>Disposition Tags (one per line)</Label><Textarea rows={3} data-testid="voice-solutions-disposition-tags-input" value={toMultiline(activeSolution.disposition_tags)} onChange={(e) => updateActiveSolution({ disposition_tags: fromMultiline(e.target.value) })} /></div>

                  <div className="border rounded p-3 space-y-3" data-testid="voice-solutions-live-interaction-section">
                    <div className="flex flex-wrap items-center justify-between gap-2">
                      <div>
                        <Label className="text-sm font-semibold">Live Interaction Simulator</Label>
                        <p className="text-xs text-muted-foreground">Mock a live call with your mic and hear AI voice responses before connecting phone systems.</p>
                      </div>
                      <div className="flex items-center gap-2">
                        <Badge variant={isListening ? 'default' : 'secondary'} data-testid="voice-solutions-mic-status-badge">
                          Mic: {isListening ? 'Listening' : 'Idle'}
                        </Badge>
                        <Button
                          variant={interactionActive ? 'destructive' : 'default'}
                          data-testid="voice-solutions-start-interaction-button"
                          onClick={interactionActive ? stopInteraction : startInteraction}
                          disabled={isGeneratingVoice || !voiceProviderState.has_key || !activeVoiceId}
                        >
                          {interactionActive ? <Square className="w-4 h-4 mr-2" /> : <Mic className="w-4 h-4 mr-2" />}
                          {interactionActive ? 'Stop Interaction' : 'Start Interaction (Mock Call)'}
                        </Button>
                      </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-3 gap-2" data-testid="voice-solutions-sim-context-fields">
                      <div>
                        <Label className="text-xs">Caller Name</Label>
                        <Input
                          data-testid="voice-solutions-sim-caller-name-input"
                          placeholder="Jane Doe"
                          value={simContext.caller_name}
                          onChange={(e) => setSimContext((prev) => ({ ...prev, caller_name: e.target.value }))}
                        />
                      </div>
                      <div>
                        <Label className="text-xs">City</Label>
                        <Input
                          data-testid="voice-solutions-sim-city-input"
                          placeholder="Los Angeles"
                          value={simContext.city}
                          onChange={(e) => setSimContext((prev) => ({ ...prev, city: e.target.value }))}
                        />
                      </div>
                      <div>
                        <Label className="text-xs">Issue</Label>
                        <Input
                          data-testid="voice-solutions-sim-issue-input"
                          placeholder="AC not cooling"
                          value={simContext.issue}
                          onChange={(e) => setSimContext((prev) => ({ ...prev, issue: e.target.value }))}
                        />
                      </div>
                    </div>

                    <div className="max-h-48 overflow-auto border rounded p-2 space-y-2" data-testid="voice-solutions-live-interaction-log">
                      {interactionLog.length === 0 ? (
                        <p className="text-xs text-muted-foreground">No interaction transcript yet.</p>
                      ) : interactionLog.map((row) => (
                        <div key={row.id} className="text-xs">
                          <span className="font-semibold">{row.speaker === 'agent' ? 'AI' : 'Caller'}:</span> {row.text}
                        </div>
                      ))}
                    </div>

                    <div className="space-y-2" data-testid="voice-solutions-preview-clips-list">
                      <p className="text-xs font-semibold">Generated Voice Clips</p>
                      {previewClips.length === 0 ? (
                        <p className="text-xs text-muted-foreground">No clips generated yet.</p>
                      ) : previewClips.map((clip) => (
                        <div key={clip.id} className="flex items-center justify-between gap-2 border rounded p-2 text-xs" data-testid={`voice-solutions-preview-clip-${clip.id}`}>
                          <span className="truncate">{clip.text}</span>
                          <div className="flex items-center gap-1">
                            <Button variant="ghost" size="sm" data-testid={`voice-solutions-preview-play-${clip.id}`} onClick={() => new Audio(clip.audio_url).play().catch(() => {})}><Play className="w-3 h-3" /></Button>
                            <Button variant="ghost" size="sm" data-testid={`voice-solutions-preview-download-${clip.id}`} onClick={() => downloadClip(clip)}><Download className="w-3 h-3" /></Button>
                            <Button variant="ghost" size="sm" data-testid={`voice-solutions-preview-delete-${clip.id}`} onClick={() => deleteClip(clip.id)}><Trash2 className="w-3 h-3" /></Button>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>

                  <Button variant="outline" onClick={deleteActiveSolution} data-testid="voice-solutions-delete-button"><Trash2 className="w-4 h-4 mr-2" />Delete Selected Solution</Button>
                </>
              )}
            </div>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2"><Link2 className="w-4 h-4" /> Campaign Mapping</CardTitle>
          <CardDescription>Pull selected voice solutions into campaigns for execution.</CardDescription>
        </CardHeader>
        <CardContent className="space-y-3" data-testid="voice-solutions-campaign-mapping">
          <div className="grid grid-cols-1 md:grid-cols-5 gap-3">
            <div>
              <Label>Campaign Type</Label>
              <Select value={campaignGroup} onValueChange={setCampaignGroup}>
                <SelectTrigger data-testid="voice-solutions-campaign-group-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="live_online">Live Online Campaigns</SelectItem>
                  <SelectItem value="all">All Campaigns</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Campaign</Label>
              <Select value={selectedCampaignId} onValueChange={setSelectedCampaignId}>
                <SelectTrigger data-testid="voice-solutions-campaign-select"><SelectValue placeholder="Select campaign" /></SelectTrigger>
                <SelectContent>
                  {filteredCampaigns.map((campaign) => (
                    <SelectItem key={campaign.id} value={campaign.id}>{campaign.client_name || 'Client'} • {campaign.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Routing Strategy</Label>
              <Select value={deliveryStrategy} onValueChange={setDeliveryStrategy}>
                <SelectTrigger data-testid="voice-solutions-routing-strategy-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="first_open">1st Open</SelectItem>
                  <SelectItem value="round_robin">Round Robin</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Fallback Strategy</Label>
              <Select
                value={deliveryFallback}
                onValueChange={setDeliveryFallback}
                disabled={deliveryStrategy !== 'first_open'}
              >
                <SelectTrigger data-testid="voice-solutions-fallback-strategy-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="round_robin">Round Robin</SelectItem>
                  <SelectItem value="none">None</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="flex items-end md:col-span-4">
              <Button onClick={assignToCampaign} disabled={assigning || !activeSolution?.id || !selectedCampaignId} data-testid="voice-solutions-assign-button">
                {assigning ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Link2 className="w-4 h-4 mr-2" />} Assign Selected Solution
              </Button>
            </div>
          </div>

          <div className="text-sm text-muted-foreground" data-testid="voice-solutions-active-mapping-status">
            {activeSolution ? `Selected solution linked to ${campaignsUsingActive.length} campaign(s).` : 'Select a solution to see campaign mapping.'}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
